import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Box,
  Tooltip,
  Alert,
  AlertTitle
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Edit } from '@mui/icons-material';

import TableCell from '../../TableCell';
import {
  getCityZonesConfigAsync,
  selectZones
} from '../../../redux/slices/zonesSlice';
import ZoneConfigModal from '../../../modals/ZoneConfigModal';
import {
  ZoneConfigResponceRow,
  ZoneType
} from '../../../resources/types/zonesTypes';
import { RootState } from '../../../redux/store';

interface IZonesConfigSectionProps {
  currentCityId: string;
  isSuperAdmin: boolean;
}

const ZonesConfigSection = ({
  currentCityId,
  isSuperAdmin
}: IZonesConfigSectionProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { zonesConfig } = useSelector(selectZones);

  const [zoneConfigEdit, setZoneConfigEdit] =
    useState<ZoneConfigResponceRow | null>(null);

  useEffect(() => {
    dispatch(getCityZonesConfigAsync(currentCityId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCityId]);

  const addedForZones = useMemo<ZoneType[]>(() => {
    const added: ZoneType[] = [];
    for (const conf of zonesConfig) {
      if (conf?.type) {
        added.push(conf?.type);
      }
    }

    return added;
  }, [zonesConfig]);

  const types = useSelector(
    (state: RootState) => state.zones.types
  ) as ZoneType[];

  const typesOptions: number = types
    .map((type: ZoneType) => ({
      value: type,
      label: type,
      hidden: addedForZones.includes(type)
    }))
    .filter(t => !t.hidden).length;

  return (
    <>
      <Alert severity="info" sx={{ marginBottom: 2 }}>
        <AlertTitle>Налаштування описів зон</AlertTitle>
        На цій сторінці ви можете налаштувати або додати власні описи для різних типів зон у вашому місті.<br /><br />
        <strong>1.</strong> Виберіть тип зони зі списку або створіть новий опис.<br />
        <strong>2.</strong> У полі "Опис" введіть текст, який буде відображатись користувачам у мобільному додатку, коли вони натискають на цю зону.<br />
        <strong>3.</strong> Якщо для певного типу зони опис ще не створено, система автоматично використовує стандартний опис за замовчуванням — але ви можете додати свій власний.<br />
        <strong>4.</strong> Під час створення чи редагування опису використовуйте кнопки UA / EN, щоб додати тексти двома мовами (українською та англійською).<br /><br />
        💡 <strong>Порада:</strong> створюйте короткі, чіткі та зрозумілі описи, наприклад:<br />
        • "Тут дозволено завершувати поїздку."<br />
        • "Рух самокатів у цій зоні заборонений."<br />
        • "У цій зоні діє обмеження швидкості 10 км/год."
      </Alert>

      {typesOptions > 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <Button
            color='primary'
            variant='contained'
            onClick={() => setZoneConfigEdit({})}
          >
            Створити
          </Button>
        </Box>
      ) : null}
      <TableContainer style={{ maxHeight: 440 }}>
        <Table stickyHeader size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Тип Зони</TableCell>
              <TableCell>Опис</TableCell>
              <TableCell>Вартість зупинки</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {zonesConfig.map(config => (
              <TableRow key={config._id}>
                <TableCell>{config.type}</TableCell>
                <TableCell>{config?.description?.ua ?? ''}</TableCell>
                <TableCell>{config.amountForStop}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    {isSuperAdmin ? (
                      <Tooltip title='Редагувати'>
                        <IconButton
                          size='medium'
                          onClick={() => {
                            setZoneConfigEdit(config);
                          }}
                          sx={{
                            bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                            color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                            borderRadius: '8px',
                            '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                          }}
                        >
                          <Edit fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ZoneConfigModal
        open={Boolean(zoneConfigEdit)}
        currentCityId={currentCityId}
        onClose={() => setZoneConfigEdit(null)}
        config={zoneConfigEdit}
        addedForZones={addedForZones}
      />
    </>
  );
};

export default ZonesConfigSection;
